import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import 'bulma/css/bulma.min.css';
import { InfoService } from '../../../services/site/InfoService';
import VideoPlayer from '../../../Components/VideoPlayer';
import digitalVideo from "../../../assets/videos/digital.mp4";
import digitalUseVideo from "../../../assets/videos/extract.mp4";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const scaleIn = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.5 } }
};

// Animated section wrapper
const AnimatedSection: React.FC<{ 
  children: React.ReactNode; 
  className?: string;
  variant?: any;
}> = ({ children, className = '', variant = fadeInUp }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={variant}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Hero Section - Product Introduction
const HeroSection: React.FC = () => {
  return (
    <AnimatedSection className="sds_ds-hero section">
      <div className="container">
        <div className="columns is-vcentered">
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="title is-1 sds_ds-hero-title">
                Digital Signature Solutions
              </h1>
              <h2 className="subtitle is-4 sds_ds-hero-subtitle mb-5">
                Transform Your Document Workflow with Secure Electronic Signatures
              </h2>
              <div className="content is-size-5 sds_ds-hero-description">
                <p className="mb-4">
                  <strong>What is a Digital Signature?</strong> It's a secure, electronic way to sign documents 
                  that carries the same legal weight as a handwritten signature - but it's faster, safer, and completely paperless.
                </p>
                <p className="mb-4">
                  HDT's Digital Signature solution empowers businesses to sign contracts, agreements, and official documents 
                  from anywhere in the world, in seconds. No more printing, scanning, or courier delays.
                </p>
              </div>
              <div className="sds_ds-feature-badges">
                <span className="tag is-large sds_ds-badge">🔒 100% Secure</span>
                <span className="tag is-large sds_ds-badge">⚡ Lightning Fast</span>
                <span className="tag is-large sds_ds-badge">⚖️ Legally Valid</span>
                <span className="tag is-large sds_ds-badge">♻️ Eco-Friendly</span>
              </div>
            </motion.div>
          </div>
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="sds_ds-hero-visual"
            >
              <div className="sds_ds-signature-animation">
                <motion.div 
                  className="sds_ds-signature-icon-large"
                  animate={{ 
                    rotate: [0, -5, 5, -5, 0],
                    scale: [1, 1.05, 1]
                  }}
                  transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                >
                  ✍️
                </motion.div>
                <div className="sds_ds-floating-docs">
                  {[1, 2, 3].map((i) => (
                    <motion.div
                      key={i}
                      className="sds_ds-floating-doc"
                      animate={{ y: [-10, 10, -10] }}
                      transition={{ duration: 2 + i, repeat: Infinity, ease: "easeInOut" }}
                    >
                      📄
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Featured Video - Product Introduction */}
        <VideoPlayer
          videoSrc={digitalVideo}
          title="What is Digital Signature?"
          description="Discover how digital signatures work and explore the powerful features of our secure e-signature platform"
          lightBackground={false}
        />
      </div>
    </AnimatedSection>
  );
};

// Benefits Overview Section
const BenefitsSection: React.FC = () => {
  const benefits = [
    { icon: '⚡', title: 'Sign in Seconds', desc: 'Complete signatures in under 30 seconds instead of days', color: '#FF9800' },
    { icon: '💰', title: 'Save 90% Costs', desc: 'Eliminate printing, paper, courier, and storage expenses', color: '#4CAF50' },
    { icon: '🌍', title: 'Sign Anywhere', desc: 'Mobile, tablet, or computer - sign from any device globally', color: '#2196F3' },
    { icon: '🔐', title: 'Bank-Grade Security', desc: '256-bit encryption with complete audit trails', color: '#9C27B0' },
    { icon: '⚖️', title: 'Legally Binding', desc: 'Compliant with IT Act 2000 and international e-signature laws', color: '#E91E63' },
    { icon: '🌱', title: 'Go Green', desc: 'Save thousands of pages annually with paperless signing', color: '#8BC34A' }
  ];

  return (
    <AnimatedSection className="sds_ds-benefits section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_ds-section-title">Why Choose Digital Signatures?</h2>
          <p className="subtitle is-5">Transform the way you do business</p>
        </div>

        <motion.div 
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {benefits.map((benefit, index) => (
            <motion.div key={index} className="column is-4" variants={scaleIn}>
              <motion.div 
                className="sds_ds-benefit-card"
                whileHover={{ y: -8, boxShadow: '0 15px 40px rgba(0,0,0,0.12)' }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="sds_ds-benefit-icon" style={{ background: benefit.color }}>
                  {benefit.icon}
                </div>
                <h3 className="title is-5 mt-4">{benefit.title}</h3>
                <p className="has-text-grey">{benefit.desc}</p>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// How It Works Section
const HowItWorksSection: React.FC = () => {
  return (
    <AnimatedSection className="sds_ds-process section sds_ds-bg-gradient">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_ds-section-title has-text-white">How Simple Is It? Just 3 Steps!</h2>
          <p className="subtitle is-5 has-text-white-ter">Get your documents signed in minutes</p>
        </div>

        <motion.div 
          className="columns is-variable is-8"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div className="column" variants={fadeInUp}>
            <div className="sds_ds-process-card">
              <div className="sds_ds-step-number">1</div>
              <div className="sds_ds-step-icon">📤</div>
              <h4 className="title is-4">Upload Document</h4>
              <p className="is-size-6 has-text-grey">Upload your PDF, Word, or any document format. Drag and drop or browse from your device.</p>
            </div>
          </motion.div>
          
          <div className="column is-narrow is-hidden-mobile sds_ds-arrow-container">
            <motion.span 
              className="sds_ds-arrow"
              animate={{ x: [0, 15, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              →
            </motion.span>
          </div>

          <motion.div className="column" variants={fadeInUp}>
            <div className="sds_ds-process-card">
              <div className="sds_ds-step-number">2</div>
              <div className="sds_ds-step-icon">🎯</div>
              <h4 className="title is-4">Add Signature Fields</h4>
              <p className="is-size-6 has-text-grey">Click to place signature fields where needed. Add dates, checkboxes, or text fields easily.</p>
            </div>
          </motion.div>

          <div className="column is-narrow is-hidden-mobile sds_ds-arrow-container">
            <motion.span 
              className="sds_ds-arrow"
              animate={{ x: [0, 15, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
            >
              →
            </motion.span>
          </div>

          <motion.div className="column" variants={fadeInUp}>
            <div className="sds_ds-process-card">
              <div className="sds_ds-step-number">3</div>
              <div className="sds_ds-step-icon">✅</div>
              <h4 className="title is-4">Send & Get Signed</h4>
              <p className="is-size-6 has-text-grey">Send via email. Recipients sign digitally in seconds. Get instant notifications.</p>
            </div>
          </motion.div>
        </motion.div>

        {/* Featured Video - Signing Process Demo */}
        <VideoPlayer
          videoSrc={digitalUseVideo}
          title="How to Use Digital Signatures"
          description="See real-world applications and discover where digital signatures transform business workflows"
          lightBackground={true}
        />
      </div>
    </AnimatedSection>
  );
};

// Multi-Page Signing Feature
const MultiPageSection: React.FC = () => {
  return (
    <AnimatedSection className="sds_ds-multipage section">
      <div className="container">
        <div className="box sds_ds-feature-box has-text-centered">
          <div className="has-text-centered mb-5">
            <h2 className="title is-2 sds_ds-section-title">
              📑 Multi-Page Document Signing Made Simple
            </h2>
            <p className="subtitle is-5">Sign ALL pages with just ONE CLICK!</p>
          </div>

          <motion.div 
            className="columns is-mobile is-centered mb-5"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {['Page 1', 'Page 2', 'Page 3', 'Pages 5-10', 'Pages 20+'].map((page, index) => (
              <motion.div key={index} className="column is-narrow" variants={scaleIn}>
                <motion.div 
                  className="sds_ds-page-icon"
                  whileHover={{ y: -10, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="sds_ds-page-emoji">📄</div>
                  <p className="is-size-7 has-text-weight-bold mt-2">{page}</p>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
          
          <motion.div 
            className="sds_ds-single-click-badge"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <span className="icon">
              <i className="fas fa-magic"></i>
            </span>
            <span>Sign ALL Pages with Just ONE CLICK!</span>
            <span className="icon">
              <i className="fas fa-magic"></i>
            </span>
          </motion.div>
          
          <div className="content has-text-centered mt-5">
            <p className="is-size-5">
              <strong>No matter if it's 2 pages or 200 pages</strong> - your signature automatically appears 
              on all required pages instantly. Say goodbye to signing each page manually!
            </p>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Use Cases Section
const UseCasesSection: React.FC = () => {
  const useCases = [
    { 
      icon: '🏠', 
      title: 'Real Estate & Property', 
      color: '#4CAF50', 
      benefits: ['Lease agreements in minutes', 'Property sale documents', 'Rental contracts', 'Power of attorney', 'Close deals 10x faster'] 
    },
    { 
      icon: '👥', 
      title: 'HR & Employment', 
      color: '#FF9800', 
      benefits: ['Offer letters instantly', 'Employment contracts', 'Non-disclosure agreements', 'Remote hiring simplified', 'Paperless onboarding'] 
    },
    { 
      icon: '⚖️', 
      title: 'Legal & Law Firms', 
      color: '#9C27B0', 
      benefits: ['Court-admissible signatures', 'Client agreements', 'Legal contracts & affidavits', 'Complete audit trail', '100% legally binding'] 
    },
    { 
      icon: '🏦', 
      title: 'Banking & Finance', 
      color: '#F44336', 
      benefits: ['Loan applications', 'Account opening forms', 'Financial agreements', 'Investment documents', 'Bank-grade security'] 
    },
    { 
      icon: '🏥', 
      title: 'Healthcare & Medical', 
      color: '#00BCD4', 
      benefits: ['Patient consent forms', 'Insurance claims', 'Medical records authorization', 'HIPAA compliant', 'Emergency approvals'] 
    },
    { 
      icon: '📈', 
      title: 'Sales & Business', 
      color: '#8BC34A', 
      benefits: ['Sales contracts & quotes', 'Vendor agreements', 'Purchase orders', 'Partnership deals', 'Instant closures'] 
    }
  ];

  return (
    <AnimatedSection className="sds_ds-use-cases section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_ds-section-title">Who Uses Digital Signatures?</h2>
          <p className="subtitle is-5">Trusted by businesses across all industries</p>
        </div>

        <motion.div 
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {useCases.map((useCase, index) => (
            <motion.div key={index} className="column is-4" variants={fadeInUp}>
              <motion.div 
                className="sds_ds-use-case-card"
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
                style={{ borderTopColor: useCase.color }}
              >
                <div className="sds_ds-use-case-icon" style={{ background: `linear-gradient(135deg, ${useCase.color}, ${useCase.color}dd)` }}>
                  {useCase.icon}
                </div>
                <h3 className="title is-5 has-text-centered mb-4">{useCase.title}</h3>
                <ul className="sds_ds-benefits-list">
                  {useCase.benefits.map((benefit, i) => (
                    <li key={i}>
                      <span className="sds_ds-check-icon" style={{ background: useCase.color }}>✓</span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Enterprise Features Section
const EnterpriseFeaturesSection: React.FC = () => {
  const features = [
    { 
      icon: '🔒', 
      title: 'Bank-Level Security', 
      desc: '256-bit encryption, secure data storage, and complete compliance with international e-signature laws.',
      points: ['Military-grade encryption', 'Data on your servers', 'Complete privacy', 'GDPR & ISO compliant']
    },
    { 
      icon: '📊', 
      title: 'Complete Audit Trail', 
      desc: 'Track every action - who signed, when, from where. Perfect for compliance and legal requirements.',
      points: ['Timestamp tracking', 'IP address logging', 'Device information', 'Downloadable reports']
    },
    { 
      icon: '🔗', 
      title: 'Seamless Integration', 
      desc: 'Integrate with your existing CRM, ERP, and business systems for smooth automated workflows.',
      points: ['Salesforce integration', 'SAP connectivity', 'Custom API access', 'Automated workflows']
    }
  ];

  return (
    <AnimatedSection className="sds_ds-enterprise section sds_ds-bg-light">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_ds-section-title">
            🏢 Enterprise-Grade Features
          </h2>
          <p className="subtitle is-5">Built for large organizations with advanced needs</p>
        </div>

        <motion.div 
          className="columns"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div key={index} className="column" variants={fadeInUp}>
              <motion.div 
                className="sds_ds-enterprise-card"
                whileHover={{ y: -5, boxShadow: '0 12px 35px rgba(33,150,243,0.15)' }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="sds_ds-enterprise-icon">{feature.icon}</div>
                <h3 className="title is-4 has-text-centered mb-3">{feature.title}</h3>
                <p className="has-text-centered has-text-grey mb-4">{feature.desc}</p>
                <ul className="sds_ds-feature-list">
                  {feature.points.map((point, i) => (
                    <li key={i}>
                      <span className="icon has-text-primary">
                        <i className="fas fa-check-circle"></i>
                      </span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Bulk Signing Section
const BulkSigningSection: React.FC = () => {
  return (
    <AnimatedSection className="sds_ds-bulk section">
      <div className="container">
        <div className="box sds_ds-feature-box sds_ds-bulk-box">
          <div className="has-text-centered mb-5">
            <h2 className="title is-2 sds_ds-section-title">
              ⚡ Bulk Signing Power
            </h2>
            <p className="subtitle is-5">Process hundreds of documents in minutes</p>
          </div>

          <motion.div 
            className="columns is-centered mb-5"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {[
              { number: '1000+', label: 'Documents at Once', icon: '📄' },
              { number: '500+', label: 'Recipients', icon: '👥' },
              { number: '< 5 Min', label: 'Processing Time', icon: '⚡' }
            ].map((stat, index) => (
              <motion.div key={index} className="column is-3" variants={scaleIn}>
                <div className="sds_ds-bulk-stat">
                  <div className="sds_ds-bulk-icon">{stat.icon}</div>
                  <motion.div 
                    className="sds_ds-bulk-number"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
                  >
                    {stat.number}
                  </motion.div>
                  <p className="has-text-weight-bold">{stat.label}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <div className="content">
            <h4 className="title is-4 has-text-centered mb-4">Perfect For:</h4>
            <div className="columns is-multiline">
              {[
                { icon: '📋', title: 'HR Mass Onboarding', desc: 'Send offer letters to 100+ employees' },
                { icon: '📢', title: 'Policy Updates', desc: 'Get signatures from all staff members' },
                { icon: '🤝', title: 'Vendor Agreements', desc: 'Sign contracts with multiple vendors' },
                { icon: '📨', title: 'Notice Distribution', desc: 'Send and get acknowledgments instantly' }
              ].map((item, index) => (
                <div key={index} className="column is-6">
                  <div className="sds_ds-bulk-use-case">
                    <span className="sds_ds-bulk-use-icon">{item.icon}</span>
                    <div>
                      <p className="has-text-weight-bold">{item.title}</p>
                      <p className="is-size-7 has-text-grey">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Comparison Section
const ComparisonSection: React.FC = () => {
  const comparisons = [
    { process: 'Time to Sign', old: '3-7 Days', new: '5 Minutes' },
    { process: 'Cost per Document', old: '₹50-200', new: '₹5-10' },
    { process: 'Storage', old: 'Physical Files', new: 'Secure Cloud' },
    { process: 'Tracking', old: 'Manual Follow-ups', new: 'Auto Notifications' },
    { process: 'Legal Validity', old: 'Valid (Slow)', new: '100% Legal & Fast' },
    { process: 'Environmental Impact', old: 'High Paper Usage', new: '100% Paperless ♻️' }
  ];

  return (
    <AnimatedSection className="sds_ds-comparison section sds_ds-bg-light">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_ds-section-title">Traditional vs. Digital Signatures</h2>
          <p className="subtitle is-5">See the dramatic difference</p>
        </div>

        <div className="box sds_ds-comparison-box">
          <div className="table-container">
            <table className="table is-fullwidth is-hoverable sds_ds-comparison-table">
              <thead>
                <tr>
                  <th className="is-size-5">Process</th>
                  <th className="has-text-centered is-size-5">❌ Traditional Way</th>
                  <th className="has-text-centered is-size-5">✅ Digital Signature</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, index) => (
                  <motion.tr 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.08 }}
                    viewport={{ once: true }}
                  >
                    <td className="has-text-weight-bold">{row.process}</td>
                    <td className="has-text-centered has-text-grey">{row.old}</td>
                    <td className="has-text-centered has-text-primary has-text-weight-bold">{row.new}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// CTA Section
const CTASection: React.FC = () => {
  return (
    <AnimatedSection className="sds_ds-cta section sds_ds-bg-gradient">
      <div className="container">
        <div className="box sds_ds-cta-box">
          <div className="has-text-centered">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="title is-1 mb-5">Ready to Transform Your Workflow?</h2>
              <p className="subtitle is-4 mb-6">
                Join thousands of businesses who have gone paperless
              </p>
              
              <div className="sds_ds-offer-badge mb-5">
                {/* <p className="is-size-3 has-text-weight-bold mb-2">🎁 Special Offer</p> */}
                <p className="is-size-4">First 2 Signatures Absolutely FREE!</p>
                <p className="is-size-6 mt-3">No credit card required • Setup in 5 minutes • Cancel anytime</p>
              </div>

              <div className="buttons is-centered are-large">
                <motion.a 
                  href="tel:+91 9553896536" 
                  className="button is-primary is-large sds_ds-cta-button"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-phone"></i>
                  </span>
                  <span>Call Now: {InfoService.mobile_no_1}</span>
                </motion.a>
                <motion.a 
                   href={InfoService.loginLink}
                  className="button is-info is-large sds_ds-cta-button"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-envelope"></i>
                  </span>
                  <span>Get Free Trial</span>
                </motion.a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Main Component
const DigitalSignatureSolutions: React.FC = () => {
  return (
    <div className="sds_ds-wrapper">
      {/* SEO Meta Tags */}
      <div style={{ display: 'none' }}>
        <h1>HDT Solutions - Digital Signature & E-Signature Platform</h1>
        <meta name="description" content="Sign documents digitally with HDT Solutions. Fast, secure, and legally binding electronic signatures for businesses. Save time and go paperless today!" />
        <meta name="keywords" content="digital signature, e-signature, electronic signature, paperless documents, online document signing, HDT Solutions" />
      </div>

      <HeroSection />
      <BenefitsSection />
      <HowItWorksSection />
      <MultiPageSection />
      <UseCasesSection />
      <EnterpriseFeaturesSection />
      <BulkSigningSection />
      <ComparisonSection />
      <CTASection />

      <style>{`
        /* Base Styles */
        .sds_ds-wrapper {
          background: #ffffff;
          min-height: 100vh;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        /* Brand Colors */
        :root {
          --primary-blue: #2196F3;
          --primary-pink: #E91E63;
          --dark-blue: #1e3c72;
          --light-blue: #E3F2FD;
          --success-green: #4CAF50;
          --light-green: #8BC34A;
        }

        /* Hero Section */
        .sds_ds-hero {
          background: linear-gradient(135deg, #E3F2FD 0%, #FCE4EC 100%);
          padding: 4rem 1.5rem;
        }

        .sds_ds-hero-title {
          color: var(--dark-blue);
          font-weight: 800;
          line-height: 1.2;
        }

        .sds_ds-hero-subtitle {
          color: var(--primary-pink);
          font-weight: 600;
        }

        .sds_ds-hero-description {
          color: #555;
          line-height: 1.8;
        }

        .sds_ds-feature-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-top: 2rem;
        }

        .sds_ds-badge {
          background: linear-gradient(135deg, var(--primary-blue), var(--primary-pink)) !important;
          color: white !important;
          font-weight: 600;
          padding: 0.75rem 1.5rem;
          border: none;
          box-shadow: 0 4px 15px rgba(33, 150, 243, 0.3);
        }

        .sds_ds-hero-visual {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 400px;
        }

        .sds_ds-signature-animation {
          position: relative;
          width: 100%;
          height: 400px;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .sds_ds-signature-icon-large {
          font-size: 150px;
          filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.1));
        }

        .sds_ds-floating-docs {
          position: absolute;
          width: 100%;
          height: 100%;
        }

        .sds_ds-floating-doc {
          position: absolute;
          font-size: 60px;
          filter: drop-shadow(0 5px 15px rgba(0, 0, 0, 0.1));
        }

        .sds_ds-floating-doc:nth-child(1) {
          top: 10%;
          left: 15%;
        }

        .sds_ds-floating-doc:nth-child(2) {
          top: 60%;
          left: 10%;
        }

        .sds_ds-floating-doc:nth-child(3) {
          top: 20%;
          right: 15%;
        }

        /* Section Title */
        .sds_ds-section-title {
          color: var(--dark-blue);
          font-weight: 700;
          position: relative;
          display: inline-block;
        }

        .sds_ds-section-title::after {
          content: '';
          display: block;
          width: 80px;
          height: 4px;
          background: linear-gradient(90deg, var(--primary-blue), var(--primary-pink));
          margin: 1rem auto 0;
          border-radius: 2px;
        }

        /* Benefits Section */
        .sds_ds-benefits {
          padding: 4rem 1.5rem;
        }

        .sds_ds-benefit-card {
          background: white;
          border-radius: 20px;
          padding: 2.5rem 2rem;
          height: 100%;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          border-top: 4px solid transparent;
        }

        .sds_ds-benefit-card:hover {
          border-top-color: var(--primary-blue);
        }

        .sds_ds-benefit-icon {
          width: 80px;
          height: 80px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 40px;
          margin: 0 auto;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }

        /* Background Gradients */
        .sds_ds-bg-gradient {
          background: linear-gradient(135deg, var(--primary-blue) 0%, #1976D2 50%, var(--primary-pink) 100%);
          color: white;
        }

        .sds_ds-bg-light {
          background: linear-gradient(135deg, #E3F2FD 0%, #F3E5F5 100%);
        }

        /* Process Section */
        .sds_ds-process {
          padding: 4rem 1.5rem;
        }

        .sds_ds-process-card {
          background: white;
          padding: 2.5rem 2rem;
          border-radius: 20px;
          text-align: center;
          height: 100%;
          box-shadow: 0 8px 25px rgba(255, 255, 255, 0.1);
          transition: all 0.3s ease;
          border: 2px solid rgba(255, 255, 255, 0.2);
        }

        .sds_ds-process-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 35px rgba(255, 255, 255, 0.2);
          border-color: rgba(255, 255, 255, 0.5);
        }

        .sds_ds-step-number {
          background: linear-gradient(135deg, var(--primary-pink), #C2185B);
          color: white;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          margin: 0 auto 1.5rem;
          font-size: 1.75rem;
          box-shadow: 0 6px 20px rgba(233, 30, 99, 0.4);
        }

        .sds_ds-step-icon {
          font-size: 60px;
          margin-bottom: 1.5rem;
        }

        .sds_ds-arrow-container {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .sds_ds-arrow {
          font-size: 3rem;
          color: white;
          font-weight: bold;
        }

        /* Multi-Page Section */
        .sds_ds-multipage {
          padding: 4rem 1.5rem;
        }

        .sds_ds-feature-box {
          background: white;
          border-radius: 25px;
          padding: 3rem 2rem;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
        }

        .sds_ds-page-icon {
          background: linear-gradient(135deg, #E3F2FD, #FCE4EC);
          padding: 1.5rem 1rem;
          border-radius: 15px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
          cursor: pointer;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .sds_ds-page-icon:hover {
          border-color: var(--primary-blue);
        }

        .sds_ds-page-emoji {
          font-size: 2.5rem;
        }

        .sds_ds-single-click-badge {
          background: linear-gradient(135deg, var(--success-green), var(--light-green));
          color: white;
          padding: 1.25rem 2.5rem;
          border-radius: 50px;
          font-size: 1.5rem;
          font-weight: bold;
          display: inline-flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 8px 30px rgba(76, 175, 80, 0.4);
          text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.2);
        }

        /* Use Cases Section */
        .sds_ds-use-cases {
          padding: 4rem 1.5rem;
        }

        .sds_ds-use-case-card {
          background: white;
          border-radius: 20px;
          padding: 2.5rem 2rem;
          height: 100%;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          border-top: 5px solid;
        }

        .sds_ds-use-case-icon {
          width: 80px;
          height: 80px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 40px;
          margin: 0 auto 1.5rem;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
        }

        .sds_ds-benefits-list {
          list-style: none;
          padding: 0;
          text-align: left;
        }

        .sds_ds-benefits-list li {
          display: flex;
          align-items: center;
          margin-bottom: 0.75rem;
          font-size: 0.95rem;
          color: #555;
        }

        .sds_ds-check-icon {
          color: white;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 0.75rem;
          font-size: 12px;
          font-weight: bold;
          flex-shrink: 0;
        }

        /* Enterprise Section */
        .sds_ds-enterprise {
          padding: 4rem 1.5rem;
        }

        .sds_ds-enterprise-card {
          background: white;
          padding: 2.5rem 2rem;
          border-radius: 20px;
          text-align: center;
          border: 3px solid #E3F2FD;
          height: 100%;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }

        .sds_ds-enterprise-card:hover {
          border-color: var(--primary-blue);
        }

        .sds_ds-enterprise-icon {
          font-size: 60px;
          margin-bottom: 1.5rem;
        }

        .sds_ds-feature-list {
          list-style: none;
          padding: 0;
          text-align: left;
        }

        .sds_ds-feature-list li {
          display: flex;
          align-items: center;
          margin-bottom: 0.75rem;
          color: #555;
        }

        .sds_ds-feature-list li .icon {
          margin-right: 0.75rem;
        }

        /* Bulk Section */
        .sds_ds-bulk {
          padding: 4rem 1.5rem;
        }

        .sds_ds-bulk-box {
          background: linear-gradient(135deg, #E8F5E9, #F1F8E9);
          border: 3px solid var(--success-green);
        }

        .sds_ds-bulk-stat {
          background: white;
          border-radius: 20px;
          padding: 2rem 1.5rem;
          text-align: center;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
        }

        .sds_ds-bulk-icon {
          font-size: 48px;
          margin-bottom: 1rem;
        }

        .sds_ds-bulk-number {
          font-size: 2.5rem;
          font-weight: bold;
          background: linear-gradient(135deg, var(--success-green), var(--light-green));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 0.5rem;
        }

        .sds_ds-bulk-use-case {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
        }

        .sds_ds-bulk-use-icon {
          font-size: 36px;
        }

        /* Comparison Section */
        .sds_ds-comparison {
          padding: 4rem 1.5rem;
        }

        .sds_ds-comparison-box {
          background: white;
          border-radius: 25px;
          padding: 2rem;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
        }

        .sds_ds-comparison-table {
          background: white;
        }

        .sds_ds-comparison-table thead {
          background: linear-gradient(135deg, var(--primary-blue), var(--primary-pink));
        }

        .sds_ds-comparison-table thead th {
          color: white !important;
          border: none !important;
          padding: 1.25rem;
        }

        .sds_ds-comparison-table tbody tr {
          transition: all 0.3s ease;
        }

        .sds_ds-comparison-table tbody tr:hover {
          background-color: #E3F2FD;
          transform: scale(1.01);
        }

        .sds_ds-comparison-table tbody td {
          padding: 1.25rem;
          vertical-align: middle;
          border-bottom: 1px solid #E3F2FD;
        }

        /* CTA Section */
        .sds_ds-cta {
          padding: 4rem 1.5rem;
        }

        .sds_ds-cta-box {
          background: white;
          border-radius: 30px;
          padding: 4rem 2rem;
          box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
        }

        .sds_ds-offer-badge {
          background: linear-gradient(135deg, #FFF9C4, #FFECB3);
          border: 3px solid #FFA726;
          border-radius: 20px;
          padding: 2rem;
          display: inline-block;
        }

        .sds_ds-cta-button {
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          padding: 1.5rem 2.5rem !important;
          border-radius: 50px !important;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
          transition: all 0.3s ease;
        }

        .sds_ds-cta-button:hover {
          box-shadow: 0 12px 35px rgba(0, 0, 0, 0.3);
        }

        /* Responsive Design */
        @media screen and (max-width: 768px) {
          .sds_ds-hero {
            padding: 2rem 1rem;
          }

          .sds_ds-hero-title {
            font-size: 2rem;
          }

          .sds_ds-signature-icon-large {
            font-size: 100px;
          }

          .sds_ds-floating-doc {
            font-size: 40px;
          }

          .sds_ds-section-title {
            font-size: 1.75rem;
          }

          .sds_ds-single-click-badge {
            font-size: 1.1rem;
            padding: 1rem 1.5rem;
          }

          .sds_ds-arrow {
            font-size: 2rem;
          }

          .sds_ds-cta-box {
            padding: 2rem 1rem;
          }

          .sds_ds-offer-badge {
            padding: 1.5rem;
          }

          .sds_ds-cta-button {
            padding: 1rem 1.5rem !important;
            font-size: 0.9rem;
          }
        }

        @media screen and (max-width: 480px) {
          .sds_ds-hero-title {
            font-size: 1.5rem;
          }

          .sds_ds-feature-badges {
            flex-direction: column;
          }

          .sds_ds-badge {
            width: 100%;
            text-align: center;
          }

          .sds_ds-bulk-number {
            font-size: 2rem;
          }
        }

        /* Print Styles */
        @media print {
          .sds_ds-bg-gradient {
            background: white;
            color: black;
          }
        }
      `}</style>
    </div>
  );
};

export default DigitalSignatureSolutions;